import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, pick } from 'ramda';

import { setBingoSchedule } from 'js/app/components/main/Games/components/GameCategory/components/BingoList/store/actions';
import { useDataFetchWithAwait } from 'js/app/hooks/useDataFetchWithAwait';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import { filterBingoSchedule } from '../helpers';
import { requestSchedule } from '../server';
import { useBingoListDispatch, useBingoListSelector } from '../store/context';

const MAX_REQUEST_COUNT = 8;

const useGetBingoRegion = () => {
    const { country, currencyCode } = useUserSelector(pick(['country', 'currencyCode']));
    const { locationJson, defaultBingoRegion } = useAppSelector(pick(['locationJson', 'defaultBingoRegion']));
    const regionItem = locationJson?.items
        .find(location => location.currency === currencyCode && location.countryCode === country);

    return useMemo(
        () => regionItem?.region || defaultBingoRegion,
        [defaultBingoRegion, regionItem]
    );
};

export const useBingoSchedule = ({
    lobbyName = 'DesktopBingo',
    minCount = 12,
    requestCount = minCount * 2
}) => {
    const { bingoSchedule } = useBingoListSelector();
    const { casinoName, gamesConfiguration } = useAppSelector(pick(['casinoName', 'gamesConfiguration']));
    const region = useGetBingoRegion();
    const dispatch = useBingoListDispatch();

    const onResponse = useCallback((responses) => {
        const data = filterBingoSchedule({ responses, gamesConfiguration });

        dispatch(setBingoSchedule(data));

        prevBingoScheduleLength.current = data.length;

        return data;
    }, [dispatch, gamesConfiguration]);

    const params = useMemo(() => ({
        region,
        lobbyName,
        casino: casinoName,
        pageSize: requestCount
    }), [casinoName, lobbyName, region, requestCount]);

    const { fetch, state, ...other } = useDataFetchWithAwait({
        params,
        onResponse,
        fetch: requestSchedule
    });

    // initial value is 1 for first request
    const prevBingoScheduleLength = useRef(1);
    const prevTimeUpdateItems = useRef(0);
    const [timeUpdateItems, setTimeUpdateItems] = useState(Date.now());
    const requestsCount = useRef(1);
    const timeoutId = useRef(null);

    useEffect(() => {
        if (isEmpty(gamesConfiguration)) {
            return;
        }

        const checkLengthItems = (responseLength) => {
            if (responseLength < minCount) {
                requestsCount.current = Math.min(requestsCount.current + 1, MAX_REQUEST_COUNT);

                timeoutId.current = setTimeout(() => {
                    // trigger for re-fetch
                    setTimeUpdateItems(Date.now());
                    timeoutId.current = null;
                }, requestsCount.current * requestsCount.current * 1000);
            } else {
                requestsCount.current = 1;
            }
        };

        if (!state.isLoading && bingoSchedule.length < minCount) {
            // if bingoSchedule decreased or update by timer
            if (bingoSchedule.length < prevBingoScheduleLength.current || timeUpdateItems !== prevTimeUpdateItems.current) {
                prevTimeUpdateItems.current = timeUpdateItems;

                clearTimeout(timeoutId.current);
                timeoutId.current = null;

                fetch({ paramsFetch: params, isUpdate: true }).then(response => {
                    checkLengthItems(response.length);
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bingoSchedule.length, minCount, params, timeUpdateItems, gamesConfiguration]);

    return useMemo(() => ({
        fetch,
        state: {
            ...state,
            data: bingoSchedule
        },
        ...other
    }), [bingoSchedule, fetch, other, state]);
};
